/**
 * Create Document Page
 * Multi-step wizard: Select Template → Document Details → Upload Files → Review & Create
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseLayout } from '@/layouts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  FileText,
  Upload,
  ClipboardList,
  FolderOpen,
  Calendar,
  CheckCircle2,
  Circle,
  Loader2,
  X,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDocuments } from '@/hooks/useDocuments';
import { useTemplates } from '@/hooks/useTemplates';
import { ROUTES, getDocumentEditorRoute } from '@/constants/routes';
import { formatDate, COUNTRIES } from '@/constants/beStudyMockData';
import { SingleFileUploader } from '@/components/be-study/common/FileUploader';
import { AIProcessingLoader } from '@/components/be-study/common/AIProcessingLoader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const STEPS = [
  { id: 'template', label: 'Select Template', icon: FolderOpen },
  { id: 'details', label: 'Document Details', icon: ClipboardList },
  { id: 'files', label: 'Upload Files', icon: Upload },
  { id: 'review', label: 'Review & Create', icon: Check },
];

function StepIndicator({ steps, currentStep }) {
  return (
    <div className="flex items-center justify-center gap-1 mb-8" data-testid="step-indicator">
      {steps.map((step, index) => {
        const StepIcon = step.icon;
        const isActive = index === currentStep;
        const isCompleted = index < currentStep;
        return (
          <React.Fragment key={step.id}>
            {index > 0 && (
              <div className={cn('h-px w-8 sm:w-16', isCompleted ? 'bg-primary' : 'bg-border')} />
            )}
            <div className="flex flex-col items-center gap-1.5" data-testid={`step-${step.id}`}>
              <div className={cn(
                'w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors',
                isActive && 'border-primary bg-primary text-primary-foreground',
                isCompleted && 'border-primary bg-primary text-primary-foreground',
                !isActive && !isCompleted && 'border-muted-foreground/30 text-muted-foreground'
              )}>
                {isCompleted ? <Check className="h-4 w-4" /> : <StepIcon className="h-4 w-4" />}
              </div>
              <span className={cn(
                'text-xs font-medium hidden sm:block',
                (isActive || isCompleted) ? 'text-primary' : 'text-muted-foreground'
              )}>
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/** Step 1: Select Template */
function TemplateStep({ templates, selectedTemplate, onSelect }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return templates;
    const q = search.toLowerCase();
    return templates.filter(t => t.name.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q));
  }, [templates, search]);

  const countryName = (code) => COUNTRIES.find(c => c.code === code)?.name || code;

  return (
    <div data-testid="template-step">
      <h2 className="text-lg font-semibold mb-1">Select a Template</h2>
      <p className="text-sm text-muted-foreground mb-4">Choose a template to base your document on.</p>
      <Input
        placeholder="Search templates..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="mb-4 max-w-sm"
        data-testid="template-search"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="template-grid">
        {filtered.map(t => (
          <Card
            key={t.id}
            className={cn(
              'cursor-pointer transition-all hover:shadow-md',
              selectedTemplate?.id === t.id && 'ring-2 ring-primary'
            )}
            onClick={() => onSelect(t)}
            data-testid={`template-card-${t.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm">{t.name}</p>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>
                </div>
                {selectedTemplate?.id === t.id && (
                  <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                )}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Badge variant="outline" className="text-xs">{countryName(t.country)}</Badge>
                <Badge variant="secondary" className="text-xs">v{t.version}</Badge>
                <span className="text-xs text-muted-foreground ml-auto">{t.sections?.length || 0} sections</span>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-2 text-center py-8 text-sm text-muted-foreground">
            No templates match your search.
          </div>
        )}
      </div>
    </div>
  );
}

/** Step 2: Document Details */
function DetailsStep({ details, onChange }) {
  return (
    <div className="max-w-lg" data-testid="details-step">
      <h2 className="text-lg font-semibold mb-1">Document Details</h2>
      <p className="text-sm text-muted-foreground mb-4">Provide basic information for your document.</p>
      <div className="space-y-4">
        <div>
          <Label htmlFor="doc-title">Document Title *</Label>
          <Input
            id="doc-title"
            placeholder="e.g., BE Study Report - Drug Name 500mg"
            value={details.title}
            onChange={e => onChange({ ...details, title: e.target.value })}
            data-testid="document-title-input"
          />
        </div>
        <div>
          <Label htmlFor="doc-due">Due Date</Label>
          <Input
            id="doc-due"
            type="date"
            value={details.dueDate}
            onChange={e => onChange({ ...details, dueDate: e.target.value })}
            data-testid="document-due-date-input"
          />
        </div>
        <div>
          <Label htmlFor="doc-notes">Notes (optional)</Label>
          <Textarea
            id="doc-notes"
            placeholder="Any additional notes about this document..."
            value={details.notes}
            onChange={e => onChange({ ...details, notes: e.target.value })}
            rows={3}
            data-testid="document-notes-input"
          />
        </div>
      </div>
    </div>
  );
}

/** Step 3: Upload Files per Section */
function FilesStep({ sections, sectionFiles, onFileChange }) {
  return (
    <div data-testid="files-step">
      <h2 className="text-lg font-semibold mb-1">Upload Source Files</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Optionally upload reference files for each section. You can skip this and add files later.
      </p>
      <div className="space-y-3">
        {sections.map((section, idx) => (
          <Card key={section.id} data-testid={`section-upload-${section.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-muted text-xs font-medium shrink-0">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium">{section.title}</p>
                    {section.required && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Required</Badge>}
                  </div>
                  {section.description && (
                    <p className="text-xs text-muted-foreground mb-2">{section.description}</p>
                  )}
                  <SingleFileUploader
                    file={sectionFiles[section.id] || null}
                    onUpload={(file) => onFileChange(section.id, file)}
                    onRemove={() => onFileChange(section.id, null)}
                    maxSize={50}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/** Step 4: Review & Create */
function ReviewStep({ template, details, sectionFiles, sections }) {
  const uploadedCount = Object.values(sectionFiles).filter(Boolean).length;
  return (
    <div data-testid="review-step">
      <h2 className="text-lg font-semibold mb-1">Review & Create</h2>
      <p className="text-sm text-muted-foreground mb-4">Verify the details before creating your document.</p>

      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Template</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="font-medium">{template?.name}</p>
            <p className="text-xs text-muted-foreground">{template?.sections?.length} sections &middot; v{template?.version}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Document Details</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-1">
            <p className="text-sm"><span className="text-muted-foreground">Title:</span> {details.title}</p>
            {details.dueDate && <p className="text-sm"><span className="text-muted-foreground">Due:</span> {formatDate(details.dueDate + 'T00:00:00Z')}</p>}
            {details.notes && <p className="text-sm"><span className="text-muted-foreground">Notes:</span> {details.notes}</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Sections ({sections.length})</CardTitle>
            <CardDescription>{uploadedCount} file{uploadedCount !== 1 ? 's' : ''} uploaded</CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {sections.map((s, i) => {
                const hasFile = !!sectionFiles[s.id];
                return (
                  <div key={s.id} className="flex items-center gap-2 text-sm" data-testid={`review-section-${s.id}`}>
                    <span className="text-xs text-muted-foreground w-5">{i + 1}.</span>
                    <span className="flex-1">{s.title}</span>
                    {hasFile ? (
                      <Badge variant="secondary" className="text-[10px]">
                        <FileText className="h-3 w-3 mr-1" />
                        {sectionFiles[s.id]?.name}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">No file</span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** Main Create Document Page */
export function CreateDocument() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { createDocument, isLoading, aiProcessingProgress } = useDocuments();
  const { activeTemplates } = useTemplates();

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [details, setDetails] = useState({ title: '', dueDate: '', notes: '' });
  const [sectionFiles, setSectionFiles] = useState({});

  const sections = selectedTemplate?.sections || [];

  const handleFileChange = useCallback((sectionId, file) => {
    setSectionFiles(prev => {
      const next = { ...prev };
      if (file) {
        next[sectionId] = file;
      } else {
        delete next[sectionId];
      }
      return next;
    });
  }, []);

  const canNext = useMemo(() => {
    if (currentStep === 0) return !!selectedTemplate;
    if (currentStep === 1) return details.title.trim().length > 0;
    return true;
  }, [currentStep, selectedTemplate, details]);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) setCurrentStep(s => s + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(s => s - 1);
  };

  const handleCreate = async () => {
    const result = await createDocument({
      title: details.title.trim(),
      templateId: selectedTemplate.id,
      templateName: selectedTemplate.name,
      templateSections: selectedTemplate.sections,
      sectionFiles: Object.fromEntries(
        Object.entries(sectionFiles).map(([k, v]) => [k, v?.name || null])
      ),
      dueDate: details.dueDate ? new Date(details.dueDate).toISOString() : null,
      createdBy: currentUser?.id,
      createdByName: currentUser?.name,
    });

    if (result.success) {
      toast.success('Document created successfully!');
      navigate(getDocumentEditorRoute(result.document.id));
    } else {
      toast.error('Failed to create document. Please try again.');
    }
  };

  return (
    <BaseLayout title="Create Document">
      <div className="p-6 max-w-3xl mx-auto" data-testid="create-document-page">
        {/* Back */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(ROUTES.DOCUMENTS)}
          className="mb-4"
          data-testid="back-to-documents-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Documents
        </Button>

        <h1 className="text-2xl font-bold mb-6" data-testid="create-document-heading">Create New Document</h1>

        <StepIndicator steps={STEPS} currentStep={currentStep} />

        {/* Loading Overlay */}
        {isLoading && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="processing-overlay">
            <Card className="w-80">
              <CardContent className="p-6 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
                <p className="text-sm font-medium">Creating document...</p>
                <p className="text-xs text-muted-foreground mt-1">Initializing sections from template</p>
                <div className="mt-3">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all duration-300"
                      style={{ width: `${aiProcessingProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{aiProcessingProgress}%</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step Content */}
        <div className="mb-8">
          {currentStep === 0 && (
            <TemplateStep
              templates={activeTemplates}
              selectedTemplate={selectedTemplate}
              onSelect={setSelectedTemplate}
            />
          )}
          {currentStep === 1 && (
            <DetailsStep details={details} onChange={setDetails} />
          )}
          {currentStep === 2 && (
            <FilesStep
              sections={sections}
              sectionFiles={sectionFiles}
              onFileChange={handleFileChange}
            />
          )}
          {currentStep === 3 && (
            <ReviewStep
              template={selectedTemplate}
              details={details}
              sectionFiles={sectionFiles}
              sections={sections}
            />
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t" data-testid="wizard-navigation">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
            data-testid="wizard-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>

          {currentStep < STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!canNext}
              data-testid="wizard-next-btn"
            >
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleCreate}
              disabled={isLoading}
              data-testid="wizard-create-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Creating...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1" /> Create Document
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </BaseLayout>
  );
}

export default CreateDocument;
